import { useEffect, useMemo, useState } from "react";
import { Sparkles, Loader2, Calendar, Cog, TrendingDown } from "lucide-react";
import { api } from "@/lib/api";

/**
 * LivePredictions — ultra-smart forecast for GET wear cycles.
 *
 * Estimates the next teeth change by averaging the intervals between
 * recent "Mark NEW" events on `/get-wear/events` and adding the mean
 * cycle length to the most recent NEW event.
 */
export default function LivePredictions() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: raw } = await api.get("/get-wear/events");
        const arr = Array.isArray(raw) ? raw : raw?.items || raw?.results || raw?.data || [];
        if (!cancelled) setData({ getEvents: arr });
      } catch {
        if (!cancelled) setData({ getEvents: [] });
      } finally { if (!cancelled) setBusy(false); }
    })();
    return () => { cancelled = true; };
  }, []);

  const teeth = useMemo(() => computeTeethPrediction(data), [data]);

  if (busy) {
    return (
      <section className="panel-heavy p-5" data-testid="live-predictions-loading">
        <div className="flex items-center gap-3 text-muted">
          <Loader2 className="w-5 h-5 animate-spin" /> Crunching predictions…
        </div>
      </section>
    );
  }

  return (
    <section className="panel-heavy p-5 md:p-6" data-testid="live-predictions">
      <div className="flex items-center gap-3 mb-4">
        <div
          className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0"
          style={{ background: "rgba(245,130,32,0.15)", color: "var(--primary)", border: "1px solid rgba(245,130,32,0.3)" }}
        >
          <Sparkles className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-muted">Ultra-smart forecast</div>
          <div className="font-display font-black text-xl tracking-tight">Live Predictions</div>
        </div>
      </div>

      <div className="panel p-4" data-testid="prediction-teeth">
        <div className="flex items-center gap-2 mb-2">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center"
               style={{ background: "rgba(245,130,32,0.15)", color: "var(--primary)" }}>
            <Cog className="w-4 h-4" />
          </div>
          <div className="text-[10px] uppercase tracking-widest font-black text-muted">Next Teeth Change</div>
        </div>
        {teeth.eta ? (
          <>
            <div className="font-display font-black text-3xl tracking-tight tabular-nums" style={{ color: "var(--primary)" }}>
              {teeth.etaDays === 0 ? "Today" : teeth.etaDays === 1 ? "Tomorrow" : `${teeth.etaDays}d`}
            </div>
            <div className="text-xs text-muted font-mono-tight mt-1">
              <Calendar className="w-3 h-3 inline mr-1" /> {teeth.etaLabel} · avg cycle {teeth.avgDays}d
              <span className="ml-2">({teeth.samples} sample{teeth.samples === 1 ? "" : "s"})</span>
            </div>
          </>
        ) : (
          <>
            <div className="font-display font-black text-lg text-muted">Not enough history yet</div>
            <div className="text-xs text-muted mt-1">Need at least 2 full cycles to forecast.</div>
          </>
        )}
      </div>

      <div className="text-[10px] uppercase tracking-widest text-muted mt-3 flex items-center gap-2">
        <TrendingDown className="w-3 h-3" /> Auto-updated · derived live from your prod data
      </div>
    </section>
  );
}

function computeTeethPrediction(data) {
  if (!data) return { eta: null };
  const events = (data.getEvents || [])
    .filter((e) => {
      const t = String(e.type || e.event || e.action || "").toLowerCase();
      return t.includes("new") || t.includes("replace") || t.includes("change");
    })
    .map((e) => new Date(e.created_at || e.date || e.ts))
    .filter((d) => !isNaN(d));
  if (events.length < 2) return { eta: null };
  events.sort((a, b) => a - b);
  const cycles = [];
  for (let i = 1; i < events.length; i++) {
    const diffDays = (events[i] - events[i - 1]) / 86400000;
    if (diffDays > 0.5 && diffDays < 400) cycles.push(diffDays);
  }
  if (!cycles.length) return { eta: null };
  const avg = cycles.reduce((a, b) => a + b, 0) / cycles.length;
  const last = events[events.length - 1];
  const eta = new Date(last.getTime() + avg * 86400000);
  const etaDays = Math.max(0, Math.round((eta - Date.now()) / 86400000));
  return {
    eta,
    etaDays,
    etaLabel: eta.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }),
    avgDays: Math.round(avg),
    samples: cycles.length,
  };
}
